// Every permission code the backend grants. Check them with usePermissions(), which only accepts this enum, so a code has exactly one name and a typo fails to compile.
export enum Permission {
  // Navigation, system settings and contacts
  VIEW_ROLE_MANAGEMENT = 'VIEWROLE',
  // Gates the System Settings → Team Management tab and route. VIEWTEAM used to; reading a team is open to everyone now and that permission is retired.
  //
  // EDITTEAM is the nearest thing to "manages teams", not the only thing protecting the page — ItemList already gates its edit icon on EDITTEAM and its Create New Team tile on CREATETEAM,
  // and a card click opens the team read-only with no permission at all. So this keeps Team Management an admin entry point; it is not what stands between a user and team data.
  MANAGE_TEAM = 'EDITTEAM',
  CREATE_TEAM = 'CREATETEAM',
  CREATE_ROLE = 'CREATEROLE',
  EDIT_ROLE = 'EDITROLE',
  DELETE_ROLE = 'DELETEROLE',
  EDIT_USER = 'EDITUSER',
  VIEW_USER_MANAGEMENT = 'VIEWUSER',
  VIEW_CONTACT_PASSENGERS = 'VIEWCONTACTPASSENGER',
  CREATE_TRIP = 'CREATETRIP',
  VIEW_TRIP = 'VIEWTRIP',
  VIEW_TASKS = 'VIEW_TASKS',
  VIEW_JET_CARD_AGREEMENT = 'VIEWJCAGR',
  VIEW_SALES_DASHBOARD = 'SALESDASHBOARD',
  VIEW_FINANCE_DASHBOARD = 'FINANCEDASHBOARD',
  VIEW_LEADERSHIP_DASHBOARD = 'LEADERSHIPDASHBOARD',
  VIEW_LEADERBOARD_DASHBOARD = 'LEADERBOARDDASHBOARD',
  CREATE_CONTACT = 'CREATECONTACT',
  CREATE_JET_CARD_AGREEMENT = 'CREATEJCAGR',
  EDIT_JET_CARD_AIRCRAFT_RATE = 'JCAGRAIRCRAFTRATE',
  EDIT_OPERATOR_COST = 'EDITOPERATORCOST',
  EDIT_CONTACT_NAME_TYPE = 'EDITCONTACTNAMETYPE',
  UNMARK_PURCHASED_LEAD = 'UNMARKPURCHASEDLEAD',
  EDIT_EMAIL_AND_PHONE = 'EDITEMAILANDPHONE',
  EDIT_SIGNIFICANT_OTHER_EMAIL_AND_PHONE = 'EDITSIGNIFICANTOTHEREMAILPHONE',
  REMOVE_CONTACT_ASSIGNMENT = 'REMOVECONTACTASSIGNMENT',
  EDIT_CONTACT_IDENTITY_DOCUMENT = 'EDITCONTACTIDENTITYDOCUMENT',
  EDIT_CONTACT_SALESMATE_ID = 'EDITCONTACTSALESMATEID',
  MANAGE_COPILOT_INVITES = 'MANAGECOPILOTINVITES',
  EDIT_COMMUNICATION_PREFERENCES = 'MODIFYCOMMUNICATIONPREFERENCES',
  VIEW_TRIP_REPORTS = 'VIEWTRIPREPORTS',
  MANAGE_TASK_TEMPLATES = 'MANAGE_TASK_TEMPLATES',

  // Contacts and Jet Cards
  DELETE_CONTACT = 'DELETECONTACT',
  VIEW_CONTACT_IDENTITY = 'VIEWCONTACTIDENTITY',
  EDIT_CONTACT_PERSONAL_INFO = 'EDITCONTACTPERSONALINFO',
  EDIT_CONTACT_CONTACT_INFO = 'EDITCONTACTCONTACTINFO',
  EDIT_SIGNIFICANT_OTHER_INFO = 'EDITSIGNIFICANTOTHERINFO',
  EDIT_CONTACT_IDENTITY = 'EDITCONTACTIDENTITY',
  EDIT_CONTACT_ASSIGNMENT = 'EDITCONTACTASSIGNMENT',
  CREATE_EDIT_LEADERSHIP_CREDITS = 'CREATEEDITLEADERSHIPCREDITS',
  CREATE_EDIT_AD_HOC_CREDITS = 'CREATEEDITADHOCCREDITS',
  EDIT_JET_CARD_AGREEMENT = 'EDITJCAGR',
  CANCEL_JET_CARD_AGREEMENT = 'CANCELJCAGR',
  ADD_JET_CARD_MANUAL_CREDIT = 'JCMANUALCREDIT',
  ADD_JET_CARD_MANUAL_DEBIT = 'JCMANUALDEBIT',
  ADD_JET_CARD_DEBIT_WITHOUT_TRIP = 'JCMANUALDEBITWITHOUTTRIPID',
  VIEW_JET_CARD_STATEMENT = 'JCSTATEMENT',
  DOWNLOAD_JET_CARD_CONTRACT = 'JCDOWNLOADCONTRACT',
  ADD_JET_CARD_FIRST_PAYMENT = 'JCFIRSTPAYMENT',
  EDIT_JET_CARD_MIN_FUND = 'JCAGRMINFUND',
  EDIT_JET_CARD_MIN_HOURS = 'JCAGRMINHOUR',
  SET_JET_CARD_ESCROW = 'JCAGRESCROW',
  EDIT_JET_CARD_ESCROW = 'MODIFYJCAGRESCROW',
  HIDE_JET_CARD_FUNDS_FROM_CLIENT = 'DONTSHOWFUNDSTOCLIENT',
  SET_JET_CARD_CATERING = 'JCAGRCATERING',
  EDIT_JET_CARD_CATERING = 'MODIFYJCAGRCATERING',
  BYPASS_JET_CARD_PROMOTION = 'BYPASSJCPROMOTION',
  FINALIZE_JET_CARD = 'FINALIZEJC',
  EDIT_CONTACT_PASSENGERS = 'MODIFYCONTACTPASSENGER',
  EDIT_PAYMENT_INFO = 'EDITPAYMINFO',
  EDIT_JET_CARD_TERMS = 'EDITTNC',
  EDIT_JET_CARD_BROKER_COMMISSION = 'JCEDITBROKERCOMMISSION',

  // Trips
  EXPORT_TRIPS = 'EXPORTTRIPS',
  EDIT_TRIPS = 'EDITTRIPS',
  DUPLICATE_TRIPS = 'CREATEDUPLTRIPS',
  CANCEL_TRIP = 'CANCELTRIP',
  DELETE_TRIPS = 'DELETETRIPS',
  EDIT_TRIP_LEGS = 'EDITTRIPLEGS',
  VIEW_TRIP_NOTES = 'VIEWTRIPNOTES',
  EDIT_TRIP_NOTES = 'MODIIFYTRIPNOTES',
  VIEW_CLIENT_ITINERARY = 'CLNTTRIPITINARY',
  VIEW_OPERATOR_TRIPSHEET = 'OPERTRIPITINARY',
  VIEW_TRIP_CONTRACT = 'VIEWTRIPCONTRACT',
  MANAGE_OPERATOR_CONTRACTS = 'MANAGEOPCONTRACT',
  VIEW_TRIP_SCENARIOS = 'VIEWTRIPSCENARIOS',
  VIEW_TRANSACTION_HISTORY = 'VIEWTRANSHISTORY',
  VIEW_ASSOCIATED_TRIPS = 'VIEWASSOCTRIPS',
  SKIP_CREDIT_CARD_AUTH = 'SKPCCAUTH',
  EDIT_CHARTER_TERMS = 'EDITCHTNC',
  ACCEPT_JET_CARD_TRIP_PROPOSAL = 'ACCJCTRIPPROP',
  VIEW_TRIP_ACTIVITY_HISTORY = 'VIEWACTHISTTRIP',
  EDIT_SPECIAL_EVENT_FEES = 'EDITSPECIALEVENTFEES',
  EDIT_TRIP_ETE_AND_HOURS = 'CREATEEDITTRIPETEANDHRS',
  MANAGE_TRIP_REPORTS = 'MANAGETRIPREPORTS',
  FLAG_PAID_OPERATOR_DIFFERS = 'FLAGPAIDOPERATORDIFFERS',

  // Finance
  VIEW_TRIP_FINANCE_DETAIL = 'VIEWTRIPFINANCEDETAIL',
  EDIT_LEG_TRIP_REVIEW = 'EDITLEGTRIPREVIEW',
  EDIT_LEG_FINANCE_REVIEW = 'EDITLEGFINANCEREVIEW',
  EDIT_REVENUE_TRIP_REVIEW = 'EDITREVENUETRIPREVIEW',
  EDIT_REVENUE_FINANCE_REVIEW = 'EDITREVENUEFINANCEREVIEW',
  EDIT_HANDLING_TRIP_REVIEW = 'EDITHANDLINGTRIPREVIEW',
  EDIT_HANDLING_FINANCE_REVIEW = 'EDITHANDLINGFINANCEREVIEW',
  EDIT_INTERNATIONAL_TRIP_REVIEW = 'EDITINTERNATIONALTRIPREVIEW',
  EDIT_INTERNATIONAL_FINANCE_REVIEW = 'EDITINTERNATIONALFINANCEREVIEW',
  EDIT_SPECIAL_TRIP_REVIEW = 'EDITSPECIALTRIPREVIEW',
  EDIT_SPECIAL_FINANCE_REVIEW = 'EDITSPECIALFINANCEREVIEW',
  REPORT_TRIP_ISSUE = 'RPTTRIPISSUE',
  CONFIRM_SEND_TO_FINANCE = 'CONFIRMSENDFIN',
  FINALIZE_TRIP = 'SAVEFNLZTRIP',
  UNFINALIZE_TRIP = 'UNFINALZ',
  PREVIEW_FINANCE_INVOICE = 'PREVFININV',
  PREVIEW_CLIENT_INVOICE = 'PREVCLNTINV',
  VIEW_TRIP_FINANCE_LIST = 'VIEWTRIPFINANCELIST',
  VIEW_OPERATOR_FINANCE = 'VIEWOPERATORFINANCE',
  EDIT_OPERATOR_FINANCE = 'EDITOPERATORFINANCE',
  SEND_TO_BROKER_REVIEW = 'BROKERTOREV',
  EDIT_COST_AFTER_CANCELLATION = 'EDITCOSTAFTERCANX',
  EDIT_CANCELLATION_STATUS = 'EDITCANXSTATUS',
  VIEW_REDUCED_PAYOUT = 'VIEWREDUCEDPAYOUT',
  EDIT_REDUCED_PAYOUT = 'EDITREDUCEDPAYOUT',
  EDIT_PAID_BEFORE_TRIP_COMPLETION = 'EDITPAIDBEFORETRIPCOMP',

  // Configuration
  MANAGE_AIRCRAFT_RATES = 'AIRCRAFTRATE',
  MANAGE_BROKERS = 'BROKERMGMT',

  // Aviation master data
  CREATE_AIRCRAFT = 'CREATEAIRCRAFT',
  CREATE_AIRPORT = 'CREATEAIRPORT',
  CREATE_FBO = 'CREATEFBO',
  CREATE_OPERATOR = 'CREATEOPERATOR',
  DELETE_AIRCRAFT = 'DELETEAIRCRAFT',
  DELETE_AIRPORT = 'DELETEAIRPORT',
  DELETE_FBO = 'DELETEFBO',
  DELETE_OPERATOR = 'DELETEOPERATOR',
  EDIT_AIRCRAFT = 'EDITAIRCRAFT',
  EDIT_AIRPORT = 'EDITAIRPORT',
  EDIT_FBO = 'EDITFBO',
  EDIT_OPERATOR = 'EDITOPERATOR',
  VIEW_AIRCRAFT = 'VIEWAIRCRAFT',
  VIEW_AIRPORT = 'VIEWAIRPORT',
  VIEW_FBO = 'VIEWFBO',
  VIEW_OPERATOR = 'VIEWOPERATOR',

  // Promotions
  CREATE_PROMO = 'CREATEPROMO',
  DELETE_PROMO = 'DELETEPROMO',
  EDIT_PROMO = 'EDITPROMO',
  END_PROMO = 'ENDPROMO',
  PAUSE_PROMO = 'PAUSEPROMO',
  VIEW_PROMO = 'VIEWPROMO',

  // Assignments
  ADD_ASSIGNMENT = 'ADDASSIGNMENT',
  EDIT_ASSIGNMENT = 'EDITASSIGNMENT',
  REMOVE_ASSIGNMENT = 'REMOVEASSIGNMENT',
  VIEW_ASSIGNMENTS = 'VIEWASSIGNMENTS',

  // Other
  MANAGE_JET_CARD_STATEMENTS = 'JCMANAGESTATEMENTS',
  RESEND_JET_CARD_AGREEMENT = 'RESENDJCAGR',
  VIEW_CONTACT = 'VIEWCONTACT',
}

// Navigation modules and sub-modules the backend grants, separate from permission codes
export enum MenuModule {
  CONTACTS = 'CONTACTS',
  DASHBOARD = 'DASHBOARD',
  FINANCIAL = 'FINANCIAL',
  INSIGHTS = 'INSIGHTS',
  JET_CARDS = 'JC',
  SYSTEM_SETTINGS = 'SYSTEMSETTING',
  TRIPS = 'TRIPS',
}

export enum SubMenu {
  DASHBOARD = 'DASHBOARD',
  REPORT = 'REPORT',
  ROLE_MANAGEMENT = 'ROLEMANAGEMENT',
  TEAM_MANAGEMENT = 'TEAMMANAGEMENT',
  USER_MANAGEMENT = 'USERMANAGEMENT',
  ACCOUNT_MANAGEMENT = 'ACCOUNTMANAGEMENT',
}
